import { z } from 'zod'

import { PlaybookEditSuggestion } from '../features/taskpane/utils/playbook-utils'

const PlaybookRuleSchema = z.object({
  name: z.string().min(1, { message: 'Name cannot be empty' }),
  standardPosition: z.string().optional(),
  fallbacks: z.array(z.string()).optional().nullable(),
  acceptableDeviations: z.array(z.string()).optional().nullable(),
  unacceptableDeviations: z.array(z.string()).optional().nullable(),
  optional: z.boolean().optional(),
})
const PlaybookSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, { message: 'Name cannot be empty' }),
  created_at: z.string().datetime(),
  modified_at: z.string().datetime(),
  rules: z.array(PlaybookRuleSchema),
})
const ListPlaybooksEndpointSchema = z.object({
  playbooks: z.array(PlaybookSchema),
})

const ReviewReferenceSchema = z.object({
  pageNo: z.number(),
  text: z.string(),
})

export interface MatchedReference extends ReviewReference {
  suggestions: PlaybookEditSuggestion[]
  range: Word.Range | null
  // Unique identifier for the reference, generated when we match the reference to a range.
  // This prevents us from using hacky workarounds to uniquely identify the range.
  id: string
  // The current ID of the suggestion that is applied to the range.
  // If no suggestion is applied, this will be null.
  appliedSuggestionId: string | null
  // The original text of the reference.
  // This is used to revert the reference to its original text if the suggestion is "undone"
  originalText: string | null
}
export type StoredReviewItem = Omit<ReviewItem, 'references'> & {
  references: MatchedReference[]
  // The current ID of the reference that is highlighted.
  // If no reference is highlighted, this will be null.
  highlightedReferenceId: string | null
}
export type StoredReviewResponse = Omit<ReviewResponse, 'review'> & {
  review: StoredReviewItem[]
}

const ReviewItemSchema = z.object({
  ruleName: z.string(),
  containsClause: z.boolean(),
  status: z.enum(['acceptable', 'needs_review', 'unacceptable']),
  explanation: z.string(),
  references: z.array(ReviewReferenceSchema).optional(),
  resolved: z.boolean().optional(),
})

const ReviewResponseSchema = z.object({
  summary: z.string(),
  review: z.array(ReviewItemSchema),
  completion: z.object({
    percentage: z.number(),
    statusMessage: z.string().optional(),
  }),
})

type ReviewStatus = 'acceptable' | 'needs_review' | 'unacceptable'

type PlaybookRule = z.infer<typeof PlaybookRuleSchema>
type Playbook = z.infer<typeof PlaybookSchema>
type ListPlaybooksEndpoint = z.infer<typeof ListPlaybooksEndpointSchema>
type ReviewResponse = z.infer<typeof ReviewResponseSchema>
type ReviewItem = z.infer<typeof ReviewItemSchema>
type ReviewReference = z.infer<typeof ReviewReferenceSchema>
export {
  PlaybookSchema,
  PlaybookRuleSchema,
  ReviewResponseSchema,
  ListPlaybooksEndpointSchema,
  Playbook,
  PlaybookRule,
  ListPlaybooksEndpoint,
  ReviewStatus,
  ReviewResponse,
  ReviewItem,
  ReviewReference,
}
